import { useState } from 'react';
import { Link } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';
import { Avatar, TierBadge, deriveTier, Popover } from '@/ui';
import { Icon } from '@/icons';
import { usePreferences } from '@/hooks';
import { formatPostTime, formatPostTimeParts } from '@/core/lib/formatPostTime';
import { EditHistoryPopover } from '@/features/post-detail/EditHistoryPopover';
import type { Post } from '@/core/models/post';

export interface AuthorLinkProps {
  post: Post;
  size?: number;
}

// ─── Styled primitives ────────────────────────────────────────────────────────

const Root = styled.div`
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 10px;
`;

const AvatarLink = styled(Link)`
  display: inline-flex;
  flex-shrink: 0;
  text-decoration: none;
  border-radius: ${({ theme }) => theme.radius.md};

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
`;

const MetaCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
`;

const NameLine = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
`;

const NameLink = styled(Link)`
  font-family: ${({ theme }) => theme.font.display};
  font-weight: 600;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textPrimary};
  text-decoration: none;
  min-width: 0;
  overflow-wrap: anywhere;

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 1px;
    border-radius: 2px;
  }
`;

const HandleTime = styled.div`
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  column-gap: 0.9em;
  row-gap: 2px;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
  margin: 0;
  line-height: 1.4;
  overflow-wrap: anywhere;
  min-width: 0;
`;

const Handle = styled.span`
  min-width: 0;
`;

const Timestamp = styled.span`
  font-variant-numeric: tabular-nums;
  min-width: 0;
`;

const EditedBtn = styled.button`
  border: none;
  background: transparent;
  padding: 0;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
  cursor: pointer;
  font-style: italic;
  text-decoration: underline dotted;

  &:hover {
    color: ${({ theme }) => theme.color.textPrimary};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
`;

// ─── Component ────────────────────────────────────────────────────────────────

export function AuthorLink({ post, size = 32 }: AuthorLinkProps) {
  const { prefs } = usePreferences();
  const theme = useTheme();
  const [historyOpen, setHistoryOpen] = useState(false);
  const handleWithoutAt = post.handle.replace(/^@/, '');
  const profilePath = `/profile/${handleWithoutAt}`;
  // Language & Region: relative ("2h") by default, absolute local time on request.
  const timeLabel = formatPostTime(post.time, post.createdAt, prefs.timestampStyle, prefs.clockFormat);
  const timeParts = formatPostTimeParts(
    post.time,
    post.createdAt,
    prefs.timestampStyle,
    prefs.clockFormat,
  );
  const hasHistory = post.edited === true && (post.editHistory?.length ?? 0) > 0;

  return (
    <Root>
      <AvatarLink to={profilePath} aria-label={`${post.name}'s profile`}>
        <Avatar name={post.name} size={size} avatarKey={post.av} />
      </AvatarLink>

      <MetaCol>
        <NameLine>
          <NameLink to={profilePath}>{post.name}</NameLink>
          <TierBadge tier={deriveTier(post.verified)} />
          {/* The supporter mark's OWN colour token, not an accent token: the
              `accentAmber` slot resolves to the same purple `accentCyan` does,
              and `accentCyan` is what TierBadge paints the verified check —
              so the two marks were colour-identical at 13px. See
              theme/tokens.ts's `supporter`. Shape still differs too (star vs
              badge-check); colour is never the only signal. */}
          {post.supporterBadge && (
            <span style={{ display: 'inline-flex', color: theme.color.supporter }}>
              <Icon name="supporter-badge" size={13} title="Supporter" />
            </span>
          )}
        </NameLine>
        <HandleTime data-testid="author-handle-time">
          <Handle>{post.handle}</Handle>
          {timeParts.map((part) => (
            <Timestamp key={part}>{part}</Timestamp>
          ))}
          {hasHistory && (
            <Popover
              trigger={<EditedBtn type="button">Edited</EditedBtn>}
              open={historyOpen}
              onOpenChange={setHistoryOpen}
            >
              <EditHistoryPopover
                comment={{ body: post.body, time: timeLabel, editHistory: post.editHistory ?? [] }}
              />
            </Popover>
          )}
        </HandleTime>
      </MetaCol>
    </Root>
  );
}
